import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stage, setStage] = useState<"checking" | "ready" | "done" | "error">("checking");
  const [errorText, setErrorText] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");

  // Establish session from either PKCE (?code=) or implicit (#access_token=) flow
  useEffect(() => {
    (async () => {
      try {
        // PKCE flow — Supabase v2 sends ?code= in the query string
        const searchParams = new URLSearchParams(window.location.search);
        const code = searchParams.get("code");

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
          setStage("ready");
          return;
        }

        // Implicit flow — older links use #access_token= in the hash
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
        const type          = hashParams.get("type");
        const access_token  = hashParams.get("access_token");
        const refresh_token = hashParams.get("refresh_token");

        if (type !== "recovery" || !access_token || !refresh_token) {
          setStage("error");
          setErrorText("Invalid or expired reset link.");
          return;
        }
        const { error } = await supabase.auth.setSession({ access_token, refresh_token });
        if (error) throw error;
        setStage("ready");
      } catch (e: unknown) {
        setStage("error");
        setErrorText(e instanceof Error ? e.message : "Unable to establish session.");
      }
    })();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast({ title: "Password too short", description: "Use at least 8 characters.", variant: "destructive" });
      return;
    }
    if (password !== password2) {
      toast({ title: "Passwords don't match", description: "Please retype them.", variant: "destructive" });
      return;
    }
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setStage("done");
      toast({ title: "Password updated", description: "You are now signed in." });

      // Route to manufacturer portal if the user has a manufacturer_users row
      setTimeout(async () => {
        const { data: session } = await supabase.auth.getSession();
        const userId = session?.session?.user?.id;
        if (userId) {
          const { data } = await supabase
            .from("manufacturer_users")
            .select("manufacturer_id")
            .eq("user_id", userId)
            .maybeSingle();
          if (data) {
            navigate("/manufacturer/overview");
            return;
          }
        }
        navigate("/portal");
      }, 800);
    } catch (e: unknown) {
      setStage("error");
      setErrorText(e instanceof Error ? e.message : "Failed to update password.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-gray-900 border-gray-800 p-6">
        <h1 className="text-2xl font-semibold mb-2">Reset your password</h1>
        <p className="text-gray-400 mb-6">Choose a new password to access your account.</p>

        {stage === "checking" && (
          <div className="text-gray-400">Verifying reset link…</div>
        )}

        {stage === "error" && (
          <div className="rounded-md border border-red-800 bg-red-950/50 p-3 text-sm text-red-200">
            {errorText ?? "An unexpected error occurred."}
          </div>
        )}

        {stage === "ready" && (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="pw1">New password</Label>
              <Input
                id="pw1"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-gray-950 border-gray-800"
                required
                minLength={8}
              />
            </div>
            <div>
              <Label htmlFor="pw2">Confirm new password</Label>
              <Input
                id="pw2"
                type="password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                placeholder="••••••••"
                className="bg-gray-950 border-gray-800"
                required
                minLength={8}
              />
            </div>
            <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700">
              Update password
            </Button>
          </form>
        )}

        {stage === "done" && (
          <div className="text-green-300">Password updated. Redirecting…</div>
        )}
      </Card>
    </div>
  );
}
